/**
 * Door type constants - single source of truth for all door types
 */
export const DOOR_TYPES = {
  // Swing doors (single panel with hinge)
  LEFT_INSWING: 'leftInswing',
  RIGHT_INSWING: 'rightInswing',
  LEFT_OUTSWING: 'leftOutswing',
  RIGHT_OUTSWING: 'rightOutswing',

  // French doors (double panel, both swing)
  LEFT_ACTIVE_INSWING_FRENCH: 'leftActiveInswingFrench',
  RIGHT_ACTIVE_INSWING_FRENCH: 'rightActiveInswingFrench',
  LEFT_ACTIVE_OUTSWING_FRENCH: 'leftActiveOutswingFrench',
  RIGHT_ACTIVE_OUTSWING_FRENCH: 'rightActiveOutswingFrench',

  // Sliding doors (panels slide on track)
  LEFT_HANDLE_SLIDING: 'leftHandleSliding',
  RIGHT_HANDLE_SLIDING: 'rightHandleSliding',

  // Pocket doors (slide into wall cavity)
  // An overhead sectional door. Its own type because it is neither a
  // swing nor a slider: the panel lifts out of the opening on a track.
  // Width alone used to classify it as sliding.
  GARAGE: 'garageDoor',

  LEFT_HANDLE_POCKET: 'leftHandlePocket',
  RIGHT_HANDLE_POCKET: 'rightHandlePocket',

  // Barn doors (a slab on a surface track). The values contain "Sliding" ON
  // PURPOSE: every renderer that asks `isSlidingDoor` draws a barn door as a
  // slider until it has a drawing of its own (`@shared-lib/openings/doorTypes`,
  // which this copy must equal — `doorTypes.parity.test.js`).
  LEFT_BARN: 'leftHandleBarnSliding',
  RIGHT_BARN: 'rightHandleBarnSliding',

  // Bifold doors. They match no other family; `DoorWithTrim` draws a styled
  // one as a shut leaf, and nothing auto-opens one.
  LEFT_BIFOLD: 'leftBifold',
  RIGHT_BIFOLD: 'rightBifold',
};

// Helper to check door category
export const isSwingDoor = (doorType) =>
  (doorType?.includes('Inswing') || doorType?.includes('Outswing')) &&
  !doorType?.includes('French');

export const isFrenchDoor = (doorType) => doorType?.includes('French');

export const isSlidingDoor = (doorType) => doorType?.includes('Sliding');

export const isPocketDoor = (doorType) => doorType?.includes('Pocket');

export const isGarageDoor = (doorType) => doorType === DOOR_TYPES.GARAGE;

export const isBarnDoor = (doorType) => String(doorType ?? '').includes('Barn');

export const isBifoldDoor = (doorType) =>
  String(doorType ?? '').includes('Bifold');

export const isInswing = (doorType) => doorType?.includes('Inswing');

export const isOutswing = (doorType) => doorType?.includes('Outswing');

export const isLeftHanded = (doorType) =>
  doorType?.startsWith('left') || doorType?.startsWith('Left');

/**
 * Whether a door must render through the styled DoorWithTrim path.
 *
 * True for any explicit style, and unconditionally for French, sliding,
 * and pocket doors — their multi-panel geometry only exists in the styled
 * renderer, so the simple single-slab fallback would draw them as one
 * solid panel spanning the full opening.
 */
// Doors whose open/close animation the renderer actually supports: hinged
// (Inswing / Outswing, French actives included), sliding and pocket all
// animate off the same doorOpenState progress in `Door.jsx`. Gates both the
// first-person proximity auto-open and the click-the-handle toggle — a type
// outside this set would flip to "open" with no animation and no visible
// panel movement, leaving an invisible hole the camera can walk through.
export const isAutoOpenDoorType = (doorType) =>
  typeof doorType === 'string' &&
  (doorType.includes('Inswing') ||
    doorType.includes('Outswing') ||
    /sliding/i.test(doorType) ||
    /pocket/i.test(doorType));

/**
 * What a door LOOKS like when nothing has been chosen for it.
 *
 * This is the single source for the appearance an unstyled door falls back
 * to, and both the renderer (`Door.jsx`) and the properties panel
 * (`DoorPanel.js`) read it. They used to carry their own literals and
 * disagreed on exactly one field: the panel opened with `doorColor: 'wood'`
 * selected on a door the renderer was drawing white. Worse, the panel
 * persisted its whole settings object on every edit, so picking a trim colour
 * stamped that invented "wood" onto the door for real.
 *
 * The rule that keeps them honest: a default is DISPLAYED, never SEEDED into
 * editable state. The panel holds `undefined` for a field the door does not
 * have, resolves through here only to draw the control, and so has nothing
 * invented to write back.
 */
/**
 * How many leaves a slider or pocket door has when nothing is stored.
 *
 * Family-dependent, and that is the point: a sliding patio unit is a pair, a
 * pocket door is a single leaf. `setFamily` in the panel has encoded exactly
 * that split since it was written (`sliding → 2`, `pocket → 1`), so this is
 * lifting a rule the app already follows rather than inventing one — while
 * `Door.jsx` carried a flat `|| 1` and the panel's own Panels row a flat
 * `|| 2`. A stored slider with no count therefore showed "2" selected and
 * drew ONE leaf.
 *
 * @param {string} doorType
 * @returns {number}
 */
export const defaultSlidingPanelCount = (doorType) =>
  isPocketDoor(doorType) || isBarnDoor(doorType) ? 1 : 2;

export const DOOR_APPEARANCE_DEFAULTS = {
  style: 'singlePanel',
  doorColor: 'white',
  handleStyle: 'lever',
  handleColor: 'chrome',
  hasThreshold: false,
  // Casing. Same treatment as the rest: seeded, these four stamped
  // simple/simple/white/white onto a door that had never been cased, on any
  // unrelated edit. They agree with `DoorWithTrim`'s own fallbacks, so this
  // was a silent write rather than a visible lie — one literal-drift away
  // from being both.
  trimStyleSideA: 'simple',
  trimStyleSideB: 'simple',
  trimColorSideA: 'white',
  trimColorSideB: 'white',
};

// A sectional garage door is a stack of full-width courses; 21" is the
// standard course. The 3D model and the elevation schematic must draw the
// SAME number of them, so the count lives here rather than in either.
export const GARAGE_SECTION_HEIGHT_M = 21 * 0.0254;

const GARAGE_SECTION_MIN = 3;

const GARAGE_SECTION_MAX = 6;

/** Whole courses nearest the standard height, for an opening height. */
export function garageSectionCount(heightMeters) {
  if (!Number.isFinite(heightMeters) || heightMeters <= 0) {
    return 4;
  }
  return Math.min(
    GARAGE_SECTION_MAX,
    Math.max(
      GARAGE_SECTION_MIN,
      Math.round(heightMeters / GARAGE_SECTION_HEIGHT_M)
    )
  );
}

// Door HARDWARE the 3D model and the elevation schematic must agree on, so
// each number lives here rather than in either.
//
// Centre height of every piece of door hardware, measured from the FLOOR:
// the standard 36". Swing levers/knobs, sliding finger pulls and the pocket
// edge pull all sit here, so a house full of mixed door types reads as one
// consistent line.
export const DOOR_HANDLE_HEIGHT_M = 0.9144;

// A standard 3½" butt hinge.
export const DOOR_HINGE_BARREL_HEIGHT_M = 0.089;

/**
 * Hinge centres for a leaf, measured DOWN from its top: the top hinge
 * 7" from the top, the bottom 11" from the bottom, the third midway.
 */
export const doorHingeCentersFromTop = (leafHeightMeters) => [
  0.178,
  leafHeightMeters / 2,
  leafHeightMeters - 0.279,
];

export const usesStyledDoorRender = (doorData = {}) =>
  Boolean(
    (doorData.style && doorData.style !== 'none') ||
      isFrenchDoor(doorData.doorType) ||
      isSlidingDoor(doorData.doorType) ||
      isPocketDoor(doorData.doorType) ||
      isGarageDoor(doorData.doorType)
  );

// Swing door configuration - explicit mapping for each type
// hingeLeftOnPriorityRoomWall: true means hinge is on the LEFT side of the wall
//   when viewed from inside the priority room
// swingAngle: positive = counterclockwise (inward), negative = clockwise (outward)
export const SWING_DOOR_CONFIG = {
  [DOOR_TYPES.LEFT_INSWING]: {
    hingeLeftOnPriorityRoomWall: false,
    swingAngle: Math.PI / 2,
  },
  [DOOR_TYPES.RIGHT_INSWING]: {
    hingeLeftOnPriorityRoomWall: true,
    swingAngle: Math.PI / 2,
  },
  [DOOR_TYPES.LEFT_OUTSWING]: {
    hingeLeftOnPriorityRoomWall: true,
    swingAngle: -Math.PI / 2,
  },
  [DOOR_TYPES.RIGHT_OUTSWING]: {
    hingeLeftOnPriorityRoomWall: false,
    swingAngle: -Math.PI / 2,
  },
};

// Helper to get swing door config with resolved hinge position for rendering
// Returns { hingeAtStart, swingAngle } where hingeAtStart is resolved based on
// the wallFacesPriorityRoom context
export const getSwingDoorConfig = (doorType, wallFacesPriorityRoom) => {
  const config = SWING_DOOR_CONFIG[doorType];
  if (!config) {
    // Default to left inswing if not found
    return { hingeAtStart: true, swingAngle: Math.PI / 2 };
  }

  // If wallFacesPriorityRoom is not provided (legacy path), use default mapping
  // hingeLeftOnPriorityRoomWall maps to hingeAtStart when wall faces priority room
  if (wallFacesPriorityRoom == null) {
    return {
      hingeAtStart: config.hingeLeftOnPriorityRoomWall,
      swingAngle: config.swingAngle,
    };
  }

  // Resolve hinge position based on wall orientation relative to priority room.
  // "hingeAtStart" means hinge at the start endpoint (center - dir * halfWidth).
  //
  // perpDir = [-dir[1], dir[0]] (90° CCW rotation of dir).
  // When wfpr=true, perpDir faces the priority room; when false, -perpDir does.
  //
  // Standing inside the priority room and looking at the wall:
  //   - wfpr=true:  your left = -dir direction = start endpoint
  //   - wfpr=false: your left = +dir direction = end endpoint
  //
  // So "hingeLeftOnPriorityRoomWall" maps to hingeAtStart when wfpr=true,
  // but maps to hingeAtEnd (i.e., !hingeAtStart) when wfpr=false.
  //
  // The swingAngle sign is then adjusted based on hingeAtStart so the arc
  // sweeps in the correct direction: when hinge is at start, the closed door
  // points along +dir and positive swingAngle sweeps toward perpDir; when
  // hinge is at end, the closed door points along -dir and swingAngle must
  // be negated to sweep toward the same side.
  if (wallFacesPriorityRoom) {
    const hingeAtStart = config.hingeLeftOnPriorityRoomWall;
    return {
      hingeAtStart,
      swingAngle: hingeAtStart ? config.swingAngle : -config.swingAngle,
    };
  } else {
    const hingeAtStart = !config.hingeLeftOnPriorityRoomWall;
    return {
      hingeAtStart,
      swingAngle: hingeAtStart ? -config.swingAngle : config.swingAngle,
    };
  }
};

// Map to flip a swing doorType: both left↔right and inswing↔outswing
const SWING_DOOR_FLIP = {
  [DOOR_TYPES.LEFT_INSWING]: DOOR_TYPES.RIGHT_OUTSWING,
  [DOOR_TYPES.RIGHT_INSWING]: DOOR_TYPES.LEFT_OUTSWING,
  [DOOR_TYPES.LEFT_OUTSWING]: DOOR_TYPES.RIGHT_INSWING,
  [DOOR_TYPES.RIGHT_OUTSWING]: DOOR_TYPES.LEFT_INSWING,
};

/**
 * Get the effective doorType for rendering, accounting for wallFacesPriorityRoom.
 * When wallFacesPriorityRoom is false, the wall normal (and door's local +Z)
 * faces away from the priority room, so we need to flip both hinge side
 * and swing direction to render correctly.
 *
 * @param {string} doorType - The stored doorType (from priority room's perspective)
 * @param {boolean|null} wallFacesPriorityRoom - Whether wall normal faces priority room
 * @returns {string} Effective doorType for rendering
 */
export const getEffectiveDoorType = (doorType, wallFacesPriorityRoom) => {
  if (!doorType) return doorType;
  if (wallFacesPriorityRoom == null || wallFacesPriorityRoom === true) {
    return doorType;
  }
  // wallFacesPriorityRoom is false - flip both dimensions
  return SWING_DOOR_FLIP[doorType] || doorType;
};

// Display names for UI
export const DOOR_TYPE_DISPLAY_NAMES = {
  [DOOR_TYPES.LEFT_INSWING]: 'Left Inswing',
  [DOOR_TYPES.RIGHT_INSWING]: 'Right Inswing',
  [DOOR_TYPES.LEFT_OUTSWING]: 'Left Outswing',
  [DOOR_TYPES.RIGHT_OUTSWING]: 'Right Outswing',
  [DOOR_TYPES.LEFT_ACTIVE_INSWING_FRENCH]: 'French (Left Active, Inswing)',
  [DOOR_TYPES.RIGHT_ACTIVE_INSWING_FRENCH]: 'French (Right Active, Inswing)',
  [DOOR_TYPES.LEFT_ACTIVE_OUTSWING_FRENCH]: 'French (Left Active, Outswing)',
  [DOOR_TYPES.RIGHT_ACTIVE_OUTSWING_FRENCH]: 'French (Right Active, Outswing)',
  [DOOR_TYPES.LEFT_HANDLE_SLIDING]: 'Sliding (Left Handle)',
  [DOOR_TYPES.RIGHT_HANDLE_SLIDING]: 'Sliding (Right Handle)',
  [DOOR_TYPES.LEFT_HANDLE_POCKET]: 'Pocket (Left Handle)',
  [DOOR_TYPES.RIGHT_HANDLE_POCKET]: 'Pocket (Right Handle)',
  [DOOR_TYPES.GARAGE]: 'Garage',
  [DOOR_TYPES.LEFT_BARN]: 'Barn (Left Handle)',
  [DOOR_TYPES.RIGHT_BARN]: 'Barn (Right Handle)',
  [DOOR_TYPES.LEFT_BIFOLD]: 'Bifold (Left)',
  [DOOR_TYPES.RIGHT_BIFOLD]: 'Bifold (Right)',
};
